/* global gdAlertify */
// Calendario de Turnos — versión modularizada.
//
// Portado desde el monolito src/views/CalendarioTurnos.js (que sigue vivo en
// la ruta /calendario-turnos y no se toca). Este archivo es el contenedor
// raíz: estado, handlers, definición de columnas (mismo orden y lógica que
// el original) y el render de la tabla + todos los modales, que ahora viven
// en ./components/*.
//
// Único cambio de comportamiento permitido: obtenerTurnos consulta
// /calendario-turnos/lista en vez de /turnos/lista. Todas las acciones de
// escritura siguen golpeando los mismos endpoints de siempre.
import { useState, useEffect, useMemo } from "react";
import BuscarPaciente from "../components/BuscarPaciente";
import GdTable from "../components/GdTable";
import axiosInstance from "../../config/axiosConfig";
import { formatDate, formatMoney, formatSectorLabel } from "../../utils/utils";
import ModalRecordatoriosPaciente from "../components/ModalRecordatoriosPaciente";
import VacacionesModal from "../components/VacacionesModal";
import NoEnviarPacienteAuxiliarModal from "../components/NoEnviarPacienteAuxiliarModal";
import ModalSolicitudesReemplazo from "../components/ModalSolicitudesReemplazo";
import ModalAcuerdosMutuos from "../components/ModalAcuerdosMutuos";
import PatronesRotacion from "../PatronesRotacion";
import Novedades from "../Novedades";
import VisitasPacienteCoordinacion from "../VisitasPacienteCoordinacion";
import PlanificacionSector from "../PlanificacionSector";
import BusquedaAuxiliar from "../BusquedaAuxiliar";
import NominaAuxiliares from "../NominaAuxiliares";
import { useUmbralesValores } from "../../utils/umbralesValores";
import { MOSTRAR_RECORDATORIOS_VALORES } from "../../config/featureFlags";
import { isNoEnviarPacienteActiveOnDate } from "../../utils/noEnviarPaciente";

import SectoresActivosBar from "./components/SectoresActivosBar";
import { AuxiliarNombreCell, AuxiliarRutCell, NotasCell } from "./components/Cells";
import NotaModal from "./components/NotaModal";
import ColorTurnoModal from "./components/ColorTurnoModal";
import ReemplazarAuxiliar from "./components/ReemplazarAuxiliar";
import IngresoAdicional from "./components/IngresoAdicional";
import ModalAdicionales from "./components/ModalAdicionales";
import ModalValorAdicional from "./components/ModalValorAdicional";
import EliminarTurnoModal from "./components/EliminarTurnoModal";
import EliminarNotaModal from "./components/EliminarNotaModal";
import ObservationAuxiliarModal from "./components/ObservationAuxiliarModal";
import EliminarAdicionalesMasivoModal from "./components/EliminarAdicionalesMasivoModal";
import RetirarPacienteTurnoModal from "./components/RetirarPacienteTurnoModal";
import EditarPacienteModal from "./components/EditarPacienteModal";
import ResumenPacienteModal from "./components/ResumenPacienteModal";
import EditarAuxiliarModal from "./components/EditarAuxiliarModal";
import ActualizarValoresModal from "./components/ActualizarValoresModal";
import DescuentoHorasExtraModal from "./components/DescuentoHorasExtraModal";

import {
    getGdAlertify,
    getTurnoAuxiliarActualInfo,
    getValorTurnoActual,
    getValorMovilizacionActual,
    getValorColacionActual,
    getDayOfWeek,
    getColorForNombre,
    NotasText,
    useCustomMonth,
    limpiarBackdropsHuerfanos,
} from "./helpers";
import MonthInput from "../components/MonthInput";

const CalendarioTurnos = () => {
    const [searchPatient, setSearchPatient] = useState([]);
    const [startMonth, setStartMonth] = useState(useCustomMonth()); // Establecer el mes actual
    const [turnos, setTurnos] = useState([]);
    const [sectoresActivos, setSectoresActivos] = useState([]);
    const umbrales = useUmbralesValores(); // Umbrales de valores (turno/colación/movilización) configurados en Administración

    // Estados para el modal de recordatorios
    const [isRecordatoriosOpen, setIsRecordatoriosOpen] = useState(false);
    const [recordatorioPacienteId, setRecordatorioPacienteId] = useState(null);
    const [recordatorioPacienteNombre, setRecordatorioPacienteNombre] = useState("");
    const [recordatorioValorDetalle, setRecordatorioValorDetalle] = useState(null);

    const [isVacacionesOpen, setIsVacacionesOpen] = useState(false);
    const [auxiliarForVacaciones, setAuxiliarForVacaciones] = useState(null);
    const [isNoEnviarOpen, setIsNoEnviarOpen] = useState(false);
    const [noEnviarContext, setNoEnviarContext] = useState(null);

    const handleOpenRecordatorios = (pacienteId, pacienteNombre) => {
        setRecordatorioPacienteId(pacienteId);
        setRecordatorioPacienteNombre(pacienteNombre);
        setIsRecordatoriosOpen(true);
    };

    const handleOpenRecordatorioValor = (titulo, texto, row) => {
        if (!texto) return;
        setRecordatorioValorDetalle({
            titulo,
            texto,
            pacienteNombre: `${row.pacienteNombre || ""} ${row.pacienteApellido || ""}`.trim(),
        });
    };

    const handleOpenNoEnviar = (row) => {
        const auxiliarActual = getTurnoAuxiliarActualInfo(row);

        if (!auxiliarActual.id) {
            gdAlertify.notiError("No hay un auxiliar asignado a este turno.");
            return;
        }

        setSelectedRow(row);
        setNoEnviarContext({
            auxiliarID: auxiliarActual.id,
            nombreAuxiliar: auxiliarActual.nombre || "Auxiliar",
            pacienteID: row?.pacienteID || null,
            pacienteNombre: row?.pacienteNombre || "",
            fechaTurno: row?.fecha || null,
        });
        setIsNoEnviarOpen(true);
    };

    const [isReemplazoOpen, setIsReemplazoOpen] = useState(false);
    const [isAdicionalOpen, setIsAdicionalOpen] = useState(false);
    const [isNotaOpen, setIsNotaOpen] = useState(false);
    const [isEliminarNotaOpen, setIsEliminarNotaOpen] = useState(false);
    const [isValorAdicionalOpen, setIsValorAdicionalOpen] = useState(false);
    const [isAdicionalesModalOpen, setIsAdicionalesModalOpen] = useState(false);
    const [isEliminarAdicionalesMasivoOpen, setIsEliminarAdicionalesMasivoOpen] = useState(false);
    const [isObservationModalOpen, setIsObservationModalOpen] = useState(false);
    const [isColorOpen, setIsColorOpen] = useState(false);
    const [isRetiroPacienteOpen, setIsRetiroPacienteOpen] = useState(false);
    const [isSolicitudesReemplazoOpen, setIsSolicitudesReemplazoOpen] = useState(false);
    const [isAcuerdosMutuosOpen, setIsAcuerdosMutuosOpen] = useState(false);
    const [isPatronesRotacionOpen, setIsPatronesRotacionOpen] = useState(false);
    const [isNovedadesOpen, setIsNovedadesOpen] = useState(false);
    const [isVisitasPacienteOpen, setIsVisitasPacienteOpen] = useState(false);
    const [isPlanificacionSectorOpen, setIsPlanificacionSectorOpen] = useState(false);
    const [isBusquedaAuxiliarOpen, setIsBusquedaAuxiliarOpen] = useState(false);
    const [isNominaAuxiliaresOpen, setIsNominaAuxiliaresOpen] = useState(false);
    const [accionAcuerdoMutuo, setAccionAcuerdoMutuo] = useState("");
    const [contextoAcuerdoMutuo, setContextoAcuerdoMutuo] = useState(null);
    const [isEditarPacienteOpen, setIsEditarPacienteOpen] = useState(false);
    const [pacienteEditarId, setPacienteEditarId] = useState(null);
    const [isEditarAuxiliarOpen, setIsEditarAuxiliarOpen] = useState(false);
    const [auxiliarEditarId, setAuxiliarEditarId] = useState(null);
    const [isResumenPacienteOpen, setIsResumenPacienteOpen] = useState(false);
    const [resumenPacienteId, setResumenPacienteId] = useState(null);
    const [solicitudesPendientes, setSolicitudesPendientes] = useState(0);

    const [selectedRow, setSelectedRow] = useState(null);
    const [mostrarExtendido, setMostrarExtendido] = useState(false);
    const reemplazarAuxiliar = async (row) => {
        setIsReemplazoOpen(true); // Abre el modal
    };

    const ingresarAdicional = async (idPaciente) => {
        setIsAdicionalOpen(true); // Abre el modal
    }

    const ingresarNota = async (idPaciente) => {
        setIsNotaOpen(true); // Abre el modal
    };

    const cambiarColor = (row) => {
        setSelectedRow(row);
        setIsColorOpen(true);
    };

    const eliminarNota = (row) => {
        setSelectedRow(row);
        setIsEliminarNotaOpen(true);
    };

    const [isEliminarOpen, setIsEliminarOpen] = useState(false);

    const eliminarTurno = (row) => {
        setSelectedRow(row);
        setIsEliminarOpen(true);
    };

    // nuevos estados
    const [isActualizarValoresOpen, setIsActualizarValoresOpen] = useState(false);
    const [isDescuentoHorasOpen, setIsDescuentoHorasOpen] = useState(false);

    // handler
    const actualizarValores = (row) => {
        setSelectedRow(row);
        setIsActualizarValoresOpen(true);
    };

    const descontarHorasExtra = (row) => {
        setSelectedRow(row);
        setIsDescuentoHorasOpen(true);
    };

    const handleOpenObservation = (row) => {
        setSelectedRow(row);
        setIsObservationModalOpen(true);
    };

    const handleOpenRetiroPaciente = (row) => {
        if (!row?.pacienteID) {
            gdAlertify.notiError("No hay un paciente asociado a este turno.");
            return;
        }

        setSelectedRow(row);
        setIsRetiroPacienteOpen(true);
    };

    const handleOpenEditarPaciente = (row) => {
        if (!row?.pacienteID) {
            gdAlertify.notiError("No hay un paciente asociado a este turno.");
            return;
        }

        setPacienteEditarId(row.pacienteID);
        setIsEditarPacienteOpen(true);
    };

    const handleVerResumenPaciente = (row) => {
        if (!row?.pacienteID) {
            gdAlertify.notiError("No hay un paciente asociado a este turno.");
            return;
        }

        setResumenPacienteId(row.pacienteID);
        setIsResumenPacienteOpen(true);
    };

    const handleOpenEditarAuxiliar = async (row) => {
        const auxiliarActual = getTurnoAuxiliarActualInfo(row);

        if (!auxiliarActual.id && !auxiliarActual.rut) {
            gdAlertify.notiError("No hay un auxiliar asignado a este turno.");
            return;
        }

        try {
            gdAlertify.showLoader();
            let auxiliarId = null;

            if (auxiliarActual.id) {
                const response = await axiosInstance.get("/auxiliar/busqueda", {
                    params: { idAuxiliar: auxiliarActual.id }
                });
                auxiliarId = response.data?.data?.[0]?.id || null;
            }

            if (!auxiliarId && auxiliarActual.rut) {
                const response = await axiosInstance.get("/auxiliar/busqueda/texto", {
                    params: { search: auxiliarActual.rut }
                });
                const rutBuscado = auxiliarActual.rut.replace(/[.\-\s]/g, "").toLowerCase();
                const auxiliarEncontrado = (response.data?.data || []).find((auxiliar) =>
                    String(auxiliar.rut || "").replace(/[.\-\s]/g, "").toLowerCase() === rutBuscado
                );
                auxiliarId = auxiliarEncontrado?.id || null;
            }

            if (!auxiliarId) {
                gdAlertify.notiError("No se pudo identificar al auxiliar para editarlo.");
                return;
            }

            setAuxiliarEditarId(auxiliarId);
            setIsEditarAuxiliarOpen(true);
        } catch (error) {
            console.error("Error al buscar auxiliar para edición:", error);
            gdAlertify.notiError("No se pudieron cargar los datos del auxiliar.");
        } finally {
            gdAlertify.hideLoader();
        }
    };

    const handleOpenAcuerdoMutuo = (row, accion) => {
        const idAcuerdo = row?.idAcuerdo || row?.id_acuerdo || row?.acuerdoID || row?.acuerdoId;

        if (!idAcuerdo) {
            gdAlertify.notiError("Este turno no tiene asociado un ID de acuerdo mutuo.");
            return;
        }

        setAccionAcuerdoMutuo(accion);
        setContextoAcuerdoMutuo({
            idAcuerdo,
            fecha: row.fecha,
            turno: row.turno,
        });
        setIsAcuerdosMutuosOpen(true);
    };

    // Misma normalización de fila que aplica obtenerTurnos, factorizada para
    // poder reutilizarla en refrescarFila sin duplicar lógica.
    const procesarTurno = (t) => ({
        ...t,
        idAcuerdo: t.idAcuerdo || t.id_acuerdo || t.acuerdoID || t.acuerdoId || null,
        reemplazoValor: t.reemplazoValorTurno || t.reemplazoValor || 0,
        valorTotal: getValorTurnoActual(t) + getValorColacionActual(t) + getValorMovilizacionActual(t) + (t.valorOtroRt || 0)
    });

    const obtenerTurnos = async (periodo, paciente) => {
        const alertify = getGdAlertify();
        const safeAlertify = alertify || {
            showLoader: () => {},
            hideLoader: () => {},
            notiError: (message) => console.error(message),
        };
        try {
            alertify?.showLoader?.()
            const params = { periodo };
            if (paciente) {
                params.pacienteID = paciente.id;
            }

            const response = await axiosInstance.get("/calendario-turnos/lista", {
                params,
            });

            if (response.data && !response.data.error) {
                setTurnos(response.data.turnos.map(procesarTurno));
            } else {
                if (alertify?.notiError) {
                    alertify.notiError("No se pudieron cargar los turnos.");
                } else {
                    console.error("No se pudieron cargar los turnos.");
                }
            }
            alertify?.hideLoader?.()
        } catch (error) {
            console.error(error);
            if (alertify?.notiError) {
                safeAlertify.notiError("Ocurrio un error al cargar los turnos.");
            } else {
                console.error("gdAlertify is not defined");
            }
            safeAlertify.hideLoader()
        }
    };

    // Refresco puntual: trae solo el/los turno(s) de un paciente en una fecha
    // (día + noche) y reemplaza esas filas en el estado local, en vez de
    // recargar el mes completo. Se usa tras acciones "individuales" (no rango).
    // Si falta pacienteID/fecha, o el pedido falla, cae de forma segura a
    // obtenerTurnos completo para no dejar la tabla desactualizada.
    const refrescarFila = async (pacienteID, fecha) => {
        if (!pacienteID || !fecha) {
            return obtenerTurnos(startMonth, searchPatient);
        }
        try {
            const response = await axiosInstance.get("/calendario-turnos/lista", {
                params: { fecha, pacienteID },
            });

            if (!response.data || response.data.error) {
                return obtenerTurnos(startMonth, searchPatient);
            }

            const frescos = response.data.turnos.map(procesarTurno);
            const clave = (t) => `${t.fecha}|${t.tipo}|${t.es_apoyo}`;
            const frescosPorClave = new Map(frescos.map((t) => [clave(t), t]));

            setTurnos((prev) => {
                const vistos = new Set();

                // Reemplaza cada fila existente de esa fecha/paciente EN SU MISMA
                // POSICIÓN (para no "saltar" al final de la tabla). Si una fila ya
                // no viene en la respuesta fresca, se quita (fue eliminada).
                const actualizado = prev.reduce((acc, t) => {
                    const esDeEstaFila = Number(t.pacienteID) === Number(pacienteID) && t.fecha === fecha;
                    if (!esDeEstaFila) {
                        acc.push(t);
                        return acc;
                    }
                    const key = clave(t);
                    if (frescosPorClave.has(key)) {
                        acc.push(frescosPorClave.get(key));
                        vistos.add(key);
                    }
                    return acc;
                }, []);

                // Agrega al final solo los turnos realmente nuevos de esa fecha
                // (ej: se creó un turno de apoyo que antes no existía).
                const nuevos = frescos.filter((t) => !vistos.has(clave(t)));

                return [...actualizado, ...nuevos];
            });
        } catch (error) {
            console.error(error);
            return obtenerTurnos(startMonth, searchPatient);
        }
    };

    // Handler compartido para el onConfirm de los modales que operan sobre
    // un turno puntual (nota, color, reemplazo, adicional, valores, etc.).
    // Cada modal reporta { pacienteID, fecha, esRango } al confirmar: si fue
    // un cambio de rango (varias fechas) recargamos el mes completo porque
    // no sabemos de antemano qué filas cambiaron; si fue puntual, refrescamos
    // solo esa fila.
    const manejarConfirmAccion = (info) => {
        if (info && !info.esRango && info.pacienteID && info.fecha) {
            refrescarFila(info.pacienteID, info.fecha);
        } else {
            obtenerTurnos(startMonth, searchPatient);
        }
    };

    const obtenerSolicitudesPendientes = async () => {
        try {
            const response = await axiosInstance.post("/reemplazos/solicitud/lista", { todas: 0 });
            const solicitudes = response.data?.error ? [] : (response.data?.data || []);
            const pendientes = solicitudes.filter((item) => item.estado !== 1 && item.estado !== "1").length;
            setSolicitudesPendientes(pendientes);
        } catch (error) {
            console.error("Error al obtener solicitudes pendientes: ", error);
            setSolicitudesPendientes(0);
        }
    };

    const obtenerSectoresActivos = async () => {
        try {
            const response = await axiosInstance.get("/planificacion/lista", {
                params: { activo: 1 },
            });

            if (response.data && response.data.data) {
                setSectoresActivos(response.data.data);
            }
        } catch (error) {
            console.error(error);
            setSectoresActivos([]);
        }
    };

    useEffect(() => {
        obtenerTurnos(startMonth, searchPatient); // Obtener los turnos con los filtros actuales
        obtenerSolicitudesPendientes();
    }, [startMonth, searchPatient]); // Solo vuelve a ejecutarse cuando el mes o paciente cambian

    useEffect(() => {
        obtenerSectoresActivos();
    }, []);

    const handleMonthChange = (e) => {
        setStartMonth(e.target.value); // Cambiar el mes seleccionado
    };

    const handleSeleccionPaciente = (paciente) => {
        setSearchPatient(paciente); // Actualizar el paciente seleccionado
    };

    const eliminarTurnoApoyo = (row) => {
        gdAlertify.confirm({
            title: 'Eliminar Turno de Apoyo',
            message: `¿Está seguro de eliminar el turno de apoyo del ${formatDate(row.fecha)} (${row.turno === '1' ? 'Día' : 'Noche'})? Esta acción no se puede deshacer.`,
            onConfirm: async () => {
                gdAlertify.showLoader();
                try {
                    const response = await axiosInstance.post('/turnos/apoyo/eliminar', {
                        pacienteID: row.pacienteID,
                        fecha: row.fecha,
                        periodo: row.turno === '1' ? 'day' : 'night',
                    });
                    if (!response.data.error) {
                        gdAlertify.notiSuccess(response.data.message);
                        refrescarFila(row.pacienteID, row.fecha);
                    } else {
                        gdAlertify.notiError(response.data.message);
                    }
                } catch (error) {
                    gdAlertify.notiError('Error al conectar con el servidor.');
                } finally {
                    gdAlertify.hideLoader();
                }
            },
            onCancel: () => gdAlertify.notify('Cancelado', 'info')
        });
    };

    const crearTurnoApoyo = async (row) => {
        gdAlertify.confirm({
            title: "Crear Turno Apoyo",
            message: `¿Deseas crear un turno de apoyo para el paciente ${row.pacienteNombre} el ${formatDate(row.fecha)} (${row.turno === "1" ? "Día" : "Noche"})?`,
            onConfirm: async () => {
                gdAlertify.showLoader();
                try {
                    const payload = {
                        pacienteID: row.pacienteID,
                        fecha: row.fecha,
                        periodo: row.tipo // "day" o "night"
                    };

                    const response = await axiosInstance.post("/turnos/apoyo/crear", payload);

                    if (!response.data.error) {
                        gdAlertify.notiSuccess(response.data.message || "Turno de apoyo creado");
                        refrescarFila(row.pacienteID, row.fecha);
                    } else {
                        gdAlertify.notiError(response.data.message);
                    }
                } catch (error) {
                    gdAlertify.notiError("Error al crear el turno de apoyo.");
                } finally {
                    gdAlertify.hideLoader();
                }
            }
        });
    };

    const devolverReemplazo = (row) => {
        gdAlertify.confirm({
            title: "Devolver Reemplazo",
            message: `¿Está seguro de devolver el reemplazo del ${formatDate(row.fecha)} (${row.turno === "1" ? "Día" : "Noche"}) para ${row.pacienteNombre}?`,
            onConfirm: async () => {
                gdAlertify.showLoader();
                try {
                    // 🔁 Envía "row" tal cual al backend
                    const resp = await axiosInstance.post("/turnos/devolver", row);

                    if (!resp.data?.error) {
                        gdAlertify.notiSuccess(resp.data?.message || "Reemplazo devuelto correctamente.");
                        refrescarFila(row.pacienteID, row.fecha); // refresca solo esta fila
                    } else {
                        gdAlertify.notiError(resp.data?.message || "No fue posible devolver el reemplazo.");
                    }
                } catch (err) {
                    gdAlertify.notiError("Ocurrió un error al conectar con el servidor.");
                } finally {
                    gdAlertify.hideLoader();
                }
            },
            onCancel: () => {
                gdAlertify.notify("Acción cancelada", "error");
            },
        });
    };


    // Definir las columnas para GdTable
    const columns = useMemo(() => {

        //
        // 🔵  Definición de columnas extendidas
        //
        const colRutPaciente = {
            title: "RUT Paciente",
            name: "pacienteRut",
            render: (row) => <b>{row.pacienteRut}</b>,
            width: 120,
        };

        const colRutAuxiliar = {
            title: "RUT Auxiliar",
            name: "auxiliarRut",
            render: (row) => <b><AuxiliarRutCell row={row} /></b>,
            width: 120,
        };

        //
        // 🔵  Definición de columnas base
        //
        const colSectorPaciente = {
            title: "Sector",
            name: "pacienteSector",
            render: (row) => <div className="text-center">{formatSectorLabel(row.pacienteSector)}</div>,
            width: 55
        };

        const colNombrePaciente = {
            title: "Nombre Paciente",
            name: "pacienteNombre",
            render: (row) => {
                const color = getColorForNombre(row.pacienteNombre);
                // El campo se llama 'tiene_recordatorio'. 1 = Mostrar botón, 0 = No mostrar.
                const showReminder = row.tiene_recordatorio == 1;

                return (
                    <div className="d-flex align-items-center">
                        {showReminder && (
                            <div
                                className="d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    backgroundColor: '#ffc107', // Amarillo
                                    cursor: 'pointer',
                                    border: '1px solid #ced4da',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenRecordatorios(
                                        row.pacienteID,
                                        `${row.pacienteNombre} ${row.pacienteApellido || ''}`
                                    );
                                }}
                                title="Ver Recordatorio"
                            >
                                <i className="bi bi-bell-fill text-dark" style={{ fontSize: '12px' }}></i>
                            </div>
                        )}
                        <span style={{ color }}><b>{row.pacienteNombre}</b></span>
                    </div>
                );
            },
            width: 230
        };

        const colNombreAuxiliar = {
            title: "Nombre Auxiliar",
            name: "auxiliarNombre",
            render: (row) => {
                const noEnviarActivoEnFecha = isNoEnviarPacienteActiveOnDate(row, row.fecha);

                return (
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <AuxiliarNombreCell row={row} />
                        {noEnviarActivoEnFecha && (
                            <span className="badge bg-warning text-dark">
                                NO ENVIAR A ESTE DOMICILIO
                            </span>
                        )}
                    </div>
                );
            },
            getFilterValue: (row) =>
                row.reemplazoNombre && row.auxiliarID != null && row.es_reemplazo == 0
                    ? row.reemplazoNombre
                    : row.auxiliarNombre,
            width: 230
        };

        //
        // 🧩  Armado de bloques según mostrarExtendido
        //

        const fechaCompleta = {
            title: "Fecha",
            name: "fecha",
            render: (row) => (
                <span className={`${row.turno == "1" ? "text-blue" : "text-red"}`}>
                    {getDayOfWeek(row.fecha)} {formatDate(row.fecha)}
                </span>
            ),
            getFilterValue: (row) => `${getDayOfWeek(row.fecha)} ${formatDate(row.fecha)}`,
            width: 120
        };

        const nombreDia = {
            title: "Día",
            name: "fecha",
            render: (row) => (
                <span className={`${row.turno == "1" ? "text-blue" : "text-red"}`}>
                    {getDayOfWeek(row.fecha)}
                </span>
            ),
            getFilterValue: (row) => `${getDayOfWeek(row.fecha)}`,
            width: 100
        };
        const fechaDia = {
            title: "Fecha",
            name: "fecha",
            type: "date",
            render: (row) => (
                <span className={`${row.turno == "1" ? "text-blue" : "text-red"}`}>
                    {formatDate(row.fecha)}
                </span>
            ),
            getFilterValue: (row) => `${formatDate(row.fecha)}`,
            width: 120
        };

        const firstBlock = mostrarExtendido
            ? [colRutPaciente, colSectorPaciente, colNombrePaciente]
            : [colSectorPaciente, colNombrePaciente];

        const secondBlock = mostrarExtendido
            ? [colRutAuxiliar, colNombreAuxiliar, nombreDia, fechaDia]
            : [colNombreAuxiliar, fechaCompleta];

        //
        // 🔵  Resto de columnas originales
        //
        const otherColumns = [
            {
                title: "Turno",
                name: "tipo",
                render: (row) => (
                    <span className={`${row.turno == "1" ? "text-blue" : "text-red"}`}>
                        {row.turno == "1" ? "Día" : "Noche"}
                        {row.es_apoyo === 1 && (
                            <span
                                className="ms-1 badge"
                                style={{ fontSize: '0.6rem', backgroundColor: '#6f42c1', verticalAlign: 'middle' }}
                            >
                                Apoyo
                            </span>
                        )}
                    </span>
                ),
                getFilterValue: (row) => `${row.turno == "1" ? "Día" : "Noche"}${row.es_apoyo === 1 ? " Apoyo" : ""}`,
                width: 80
            },

            {
                title: "Valor Turno",
                name: "valorTurno",
                render: (row) => {
                    if (
                        row.auxiliarNombre == '' ||
                        row.auxiliarNombre == "  " ||
                        row.auxiliarNombre == null
                    ) {
                        return '';
                    }

                    const valor = getValorTurnoActual(row);
                    const recordatorioValor = (row.recordatorio_valor_turno || "").trim();
                    const umbralTurno = Number(umbrales?.turno || 0);
                    const excedeUmbral = umbralTurno > 0 && Number(valor) > umbralTurno;

                    return (
                        <div
                            className={`d-flex align-items-center justify-content-end gap-1 flex-wrap px-1 ${excedeUmbral ? "bg-row-rojo-claro rounded" : ""}`}
                            style={{ height: '100%', width: '100%' }}
                            title={excedeUmbral ? `Supera el umbral configurado ($ ${formatMoney(umbralTurno)})` : undefined}
                        >
                            <span>{formatMoney(valor) ? `$ ${formatMoney(valor)}` : ""}</span>
                            {MOSTRAR_RECORDATORIOS_VALORES && recordatorioValor && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary p-0 d-inline-flex align-items-center justify-content-center"
                                    style={{ width: 18, height: 18, lineHeight: 1 }}
                                    title="Ver recordatorio de valor turno"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenRecordatorioValor("Recordatorio valor turno", recordatorioValor, row);
                                    }}
                                >
                                    <i className="bi bi-info-circle" style={{ fontSize: 11 }}></i>
                                </button>
                            )}
                            {Number(row.horasDescuentoExtra || 0) > 0 && (
                                <div className="badge bg-warning text-dark mt-1">
                                    -{parseInt(row.horasDescuentoExtra || 0, 10)} h {
                                        row.pagadorDescuentoExtra === "familia" || row.pagadorDescuentoExtra === "paciente"
                                            ? "Familia"
                                            : row.pagadorDescuentoExtra === "ambas"
                                                ? "Ambas"
                                                : "Auxiliar"
                                    }
                                </div>
                            )}
                        </div>
                    );
                },
                width: 65,
                getFilterValue: (row) => `${getValorTurnoActual(row)}`,
            },

            {
                title: "Valor Movili..",
                name: "valorMovilizacion",
                render: (row) => {
                    if (
                        row.auxiliarNombre == '' ||
                        row.auxiliarNombre == "  " ||
                        row.auxiliarNombre == null
                    ) {
                        return '';
                    }

                    const valor = getValorMovilizacionActual(row);
                    const recordatorioValor = (row.recordatorio_movilizacion || "").trim();
                    const umbralMovilizacion = Number(umbrales?.movilizacion || 0);
                    const excedeUmbral = umbralMovilizacion > 0 && Number(valor) > umbralMovilizacion;

                    return (
                        <div
                            className={`d-flex align-items-center justify-content-end gap-1 px-1 ${excedeUmbral ? "bg-row-rojo-claro rounded" : ""}`}
                            style={{ height: '100%', width: '100%' }}
                            title={excedeUmbral ? `Supera el umbral configurado ($ ${formatMoney(umbralMovilizacion)})` : undefined}
                        >
                            <span>{Number(valor) > 0 ? `$ ${formatMoney(valor)}` : ""}</span>
                            {MOSTRAR_RECORDATORIOS_VALORES && recordatorioValor && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary p-0 d-inline-flex align-items-center justify-content-center"
                                    style={{ width: 18, height: 18, lineHeight: 1 }}
                                    title="Ver recordatorio de movilizacion"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenRecordatorioValor("Recordatorio movilizacion", recordatorioValor, row);
                                    }}
                                >
                                    <i className="bi bi-info-circle" style={{ fontSize: 11 }}></i>
                                </button>
                            )}
                        </div>
                    );
                },
                width: 65,
                getFilterValue: (row) => `${getValorMovilizacionActual(row)}`,
            },

            {
                title: "Valor Colación",
                name: "valorColacion",
                render: (row) => {
                    if (
                        row.auxiliarNombre == '' ||
                        row.auxiliarNombre == "  " ||
                        row.auxiliarNombre == null
                    ) {
                        return '';
                    }

                    const valor = getValorColacionActual(row);
                    const recordatorioValor = (row.recordatorio_colacion || "").trim();
                    const umbralColacion = Number(umbrales?.colacion || 0);
                    const excedeUmbral = umbralColacion > 0 && Number(valor) > umbralColacion;

                    return (
                        <div
                            className={`d-flex align-items-center justify-content-end gap-1 px-1 ${excedeUmbral ? "bg-row-rojo-claro rounded" : ""}`}
                            style={{ height: '100%', width: '100%' }}
                            title={excedeUmbral ? `Supera el umbral configurado ($ ${formatMoney(umbralColacion)})` : undefined}
                        >
                            <span>{Number(valor) > 0 ? `$ ${formatMoney(valor)}` : ""}</span>
                            {MOSTRAR_RECORDATORIOS_VALORES && recordatorioValor && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary p-0 d-inline-flex align-items-center justify-content-center"
                                    style={{ width: 18, height: 18, lineHeight: 1 }}
                                    title="Ver recordatorio de colacion"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenRecordatorioValor("Recordatorio colacion", recordatorioValor, row);
                                    }}
                                >
                                    <i className="bi bi-info-circle" style={{ fontSize: 11 }}></i>
                                </button>
                            )}
                        </div>
                    );
                },
                width: 65,
                getFilterValue: (row) => `${getValorColacionActual(row)}`,
            },

            {
                title: "Notas",
                name: "notes",
                render: (row) => <NotasCell row={row} />,
                getFilterValue: (row) => NotasText(row),
                width: 350
            },

            {
                name: "id",
                title: "Acción",
                render: (row) => {
                    const hasAux = row.auxiliarID != null;
                    const esApoyo = row.es_apoyo === 1;
                    const es_reemplazo = row.es_reemplazo;
                    const con_nota = row.notes.trim() !== "";
                    const esTurnoReemplazo = Boolean(
                        Number(row.es_reemplazo) === 1
                        || row.reemplazoID
                        || row.reemplazoNombre
                        || row.es_apoyo === 1
                    );
                    const puedeGestionarAcuerdo = Boolean(
                        hasAux && !esTurnoReemplazo && row.idAcuerdo
                    );

                    const handleActionSectionToggle = (event) => {
                        const currentSection = event.currentTarget;
                        if (!currentSection.open) return;

                        currentSection
                            .closest(".acciones-menu")
                            ?.querySelectorAll("details")
                            .forEach((section) => {
                                if (section !== currentSection) {
                                    section.open = false;
                                }
                            });
                    };

                    return (
                        <div className="d-flex gap-1">

                        {/* Acciones con secciones */}
                        <div className="dropdown">
                            <button
                                className="btn btn-sm btn-primary dropdown-toggle"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                Acciones
                            </button>
                            <ul className="dropdown-menu acciones-menu" style={{ minWidth: 260 }}>

                                <li>
                                    <details
                                        ref={(section) => {
                                            if (section && !section.dataset.initialized) {
                                                section.open = true;
                                                section.dataset.initialized = "true";
                                            }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        onToggle={handleActionSectionToggle}
                                    >
                                        <summary className="dropdown-item fw-semibold text-dark" style={{ cursor: "pointer" }}>
                                            <i className="bi bi-calendar3 me-2"></i>
                                            Turnos
                                        </summary>
                                        <ul className="list-unstyled mb-0">

                                <li>
                                    <button
                                        className="dropdown-item text-primary"
                                        onClick={() => { setSelectedRow(row); reemplazarAuxiliar(row.id); }}
                                    >
                                        <i className="bi bi-arrow-repeat me-2"></i>
                                        {hasAux ? "Reemplazar" : "Asignar"}
                                    </button>
                                </li>

                                {!!es_reemplazo && (
                                    <li>
                                        <button
                                            className="dropdown-item text-primary"
                                            onClick={() => { setSelectedRow(row); devolverReemplazo(row); }}
                                        >
                                            <i className="bi bi-arrow-counterclockwise me-2"></i>
                                            Devolver Reemplazo
                                        </button>
                                    </li>
                                )}

                                {!esApoyo && (
                                    <li>
                                        <button
                                            className="dropdown-item text-warning"
                                            onClick={() => crearTurnoApoyo(row)}
                                        >
                                            <i className="bi bi-person-plus me-2"></i>
                                            Turno Apoyo
                                        </button>
                                    </li>
                                )}

                                <li>
                                    <button
                                        className="dropdown-item text-success"
                                        onClick={() => {
                                            if (row.auxiliarID) {
                                                setAuxiliarForVacaciones({ id: row.auxiliarID, nombreAuxiliar: row.auxiliarNombre });
                                                setIsVacacionesOpen(true);
                                            } else {
                                                gdAlertify.notiError("No hay un auxiliar asignado a este turno.");
                                            }
                                        }}
                                    >
                                        <i className="bi bi-calendar-check me-2"></i>
                                        Vacaciones
                                    </button>
                                </li>

                                <li>
                                    <button
                                        className="dropdown-item text-secondary"
                                        onClick={() => { setSelectedRow(row); ingresarNota(row.id); }}
                                    >
                                        <i className="bi bi-pencil-square me-2"></i>
                                        Notas
                                    </button>
                                </li>

                                {con_nota && (
                                    <li>
                                        <button
                                            className="dropdown-item text-danger"
                                            onClick={() => { setSelectedRow(row); eliminarNota(row); }}
                                        >
                                            <i className="bi bi-trash me-2"></i>
                                            Eliminar Nota
                                        </button>
                                    </li>
                                )}

                                {hasAux && (
                                    <li>
                                        <button
                                            className="dropdown-item text-danger"
                                            onClick={() => { setSelectedRow(row); eliminarTurno(row); }}
                                        >
                                            <i className="bi bi-trash me-2"></i>
                                            Eliminar
                                        </button>
                                    </li>
                                )}

                                {esApoyo && (
                                    <li>
                                        <button
                                            className="dropdown-item text-danger"
                                            onClick={() => eliminarTurnoApoyo(row)}
                                        >
                                            <i className="bi bi-person-dash me-2"></i>
                                            Eliminar Turno Apoyo
                                        </button>
                                    </li>
                                )}

                                <li>
                                    <button
                                        className="dropdown-item text-primary"
                                        onClick={() => { setSelectedRow(row); obtenerTurnos(startMonth, searchPatient); }}
                                    >
                                        <i className="bi bi-arrow-repeat me-2"></i>
                                        Actualizar
                                    </button>
                                </li>

                                {puedeGestionarAcuerdo && (
                                    <>
                                        <li>
                                            <button
                                                className="dropdown-item text-primary"
                                                onClick={() => handleOpenAcuerdoMutuo(row, "actualizar")}
                                            >
                                                <i className="bi bi-file-earmark-text me-2"></i>
                                                Actualizar Acuerdo Mutuo
                                            </button>
                                        </li>

                                        <li>
                                            <button
                                                className="dropdown-item text-danger"
                                                onClick={() => handleOpenAcuerdoMutuo(row, "cerrar")}
                                            >
                                                <i className="bi bi-file-earmark-x me-2"></i>
                                                Cerrar Acuerdo Mutuo
                                            </button>
                                        </li>
                                    </>
                                )}

                                <li>
                                    <button
                                        className="dropdown-item text-primary"
                                        onClick={() => cambiarColor(row)}
                                    >
                                        <i className="bi bi-palette me-2"></i>
                                        Cambiar Color
                                    </button>
                                </li>

                                {hasAux && (
                                    <li>
                                        <button
                                            className="dropdown-item text-warning"
                                            onClick={() => handleOpenNoEnviar(row)}
                                        >
                                            <i className="bi bi-exclamation-triangle me-2"></i>
                                            No Enviar a Este Domicilio
                                        </button>
                                    </li>
                                )}
                                        </ul>
                                    </details>
                                </li>

                                <li><hr className="dropdown-divider" /></li>
                                <li>
                                    <details
                                        onClick={(e) => e.stopPropagation()}
                                        onToggle={handleActionSectionToggle}
                                    >
                                        <summary className="dropdown-item fw-semibold text-dark" style={{ cursor: "pointer" }}>
                                            <i className="bi bi-cash-coin me-2"></i>
                                            Valores
                                        </summary>
                                        <ul className="list-unstyled mb-0">
                                            {hasAux && (
                                                <li>
                                                    <button
                                                        className="dropdown-item text-dark ps-4"
                                                        onClick={() => { setSelectedRow(row); actualizarValores(row); }}
                                                    >
                                                        <i className="bi bi-currency-dollar me-2"></i>
                                                        Actualizar Valores
                                                    </button>
                                                </li>
                                            )}

                                            {hasAux && (
                                                <li>
                                                    <button
                                                        className="dropdown-item text-danger ps-4"
                                                        onClick={() => descontarHorasExtra(row)}
                                                    >
                                                        <i className="bi bi-dash-circle me-2"></i>
                                                        Descontar Horas
                                                    </button>
                                                </li>
                                            )}

                                            <li>
                                                <button
                                                    className="dropdown-item text-success ps-4"
                                                    onClick={() => { setSelectedRow(row); ingresarAdicional(row.id); }}
                                                >
                                                    <i className="bi bi-plus-circle me-2"></i>
                                                    Adicional
                                                </button>
                                            </li>

                                            {row.adicionales?.length > 0 && (
                                                <li>
                                                    <button
                                                        className="dropdown-item text-info ps-4"
                                                        onClick={() => { setSelectedRow(row); setIsAdicionalesModalOpen(true); }}
                                                    >
                                                        <i className="bi bi-list-ul me-2"></i>
                                                        Ver Adicionales
                                                    </button>
                                                </li>
                                            )}

                                            {row.adicionales?.length > 0 && (
                                                <li>
                                                    <button
                                                        className="dropdown-item text-danger ps-4"
                                                        onClick={() => { setSelectedRow(row); setIsEliminarAdicionalesMasivoOpen(true); }}
                                                    >
                                                        <i className="bi bi-trash me-2"></i>
                                                        Eliminar Adicionales
                                                    </button>
                                                </li>
                                            )}
                                        </ul>
                                    </details>
                                </li>

                                <li><hr className="dropdown-divider" /></li>
                                <li>
                                    <details
                                        onClick={(e) => e.stopPropagation()}
                                        onToggle={handleActionSectionToggle}
                                    >
                                        <summary className="dropdown-item fw-semibold text-dark" style={{ cursor: "pointer" }}>
                                            <i className="bi bi-three-dots me-2"></i>
                                            Otros
                                        </summary>
                                        <ul className="list-unstyled mb-0">
                                            <li>
                                                <button
                                                    className="dropdown-item text-primary ps-4"
                                                    onClick={() => handleVerResumenPaciente(row)}
                                                >
                                                    <i className="bi bi-file-earmark-person me-2"></i>
                                                    Resumen Paciente
                                                </button>
                                            </li>

                                            {hasAux && (
                                                <li>
                                                    <button
                                                        className="dropdown-item text-secondary ps-4"
                                                        onClick={() => handleOpenObservation(row)}
                                                    >
                                                        <i className="bi bi-chat-left-text me-2"></i>
                                                        Obs. Auxiliar
                                                    </button>
                                                </li>
                                            )}

                                            {hasAux && (
                                                <li>
                                                    <button
                                                        className="dropdown-item text-danger ps-4"
                                                        onClick={() => handleOpenRetiroPaciente(row)}
                                                    >
                                                        <i className="bi bi-person-x me-2"></i>
                                                        Retirar Paciente
                                                    </button>
                                                </li>
                                            )}
                                        </ul>
                                    </details>
                                </li>

                                <li><hr className="dropdown-divider" /></li>
                                <li>
                                    <details
                                        onClick={(e) => e.stopPropagation()}
                                        onToggle={handleActionSectionToggle}
                                    >
                                        <summary className="dropdown-item fw-semibold text-dark" style={{ cursor: "pointer" }}>
                                            <i className="bi bi-pencil-square me-2"></i>
                                            Edición
                                        </summary>
                                        <ul className="list-unstyled mb-0">
                                            <li>
                                                <button
                                                    className="dropdown-item text-success ps-4"
                                                    onClick={() => handleOpenEditarPaciente(row)}
                                                >
                                                    <i className="bi bi-person-gear me-2"></i>
                                                    Editar Paciente
                                                </button>
                                            </li>

                                            <li>
                                                <button
                                                    className="dropdown-item text-success ps-4"
                                                    onClick={() => handleOpenEditarAuxiliar(row)}
                                                >
                                                    <i className="bi bi-person-gear me-2"></i>
                                                    Editar Auxiliar
                                                </button>
                                            </li>
                                        </ul>
                                    </details>
                                </li>

                            </ul>
                        </div>

                        </div>
                    );
                },
                width: 160
            }
        ];

        //
        // 🧩 Retorna columnas completas en orden
        //
        return [
            ...firstBlock,
            ...secondBlock,
            ...otherColumns,
        ];

    }, [turnos, mostrarExtendido, umbrales]);


    return (
        <div className="container-fluid mt-4">
            <div className="card">
                <div className="card-header">
                    <h4 className="mb-0">Calendario Turnos</h4>
                </div>
                <div className="card-body">
                    <SectoresActivosBar sectores={sectoresActivos} />

                    {/* Formulario de búsqueda */}
                    <div className="row mb-4">
                        <div className="col-md-4 mb-3">
                            <BuscarPaciente onSelect={handleSeleccionPaciente} onClear={handleSeleccionPaciente} />
                        </div>
                        <div className="col-md-3 mb-3">
                            <label className="form-label">Periodo</label>
                            <MonthInput
                                className="form-control"
                                placeholder="Mes de inicio"
                                value={startMonth}
                                onChange={handleMonthChange} // Cambiar mes
                            />
                        </div>
                        <div className="col-md-2 mb-3">
                            <button
                                className="btn btn-primary w-100"
                                onClick={() => obtenerTurnos(startMonth, searchPatient)} // Ejecutar la búsqueda al hacer clic
                            >
                                Buscar Turnos
                            </button>
                        </div>
                        <div className="col-md-4 form-check ms-3">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="mostrarExtendido"
                                checked={mostrarExtendido}
                                onChange={(e) => setMostrarExtendido(e.target.checked)}
                            />
                            <label className="form-check-label ms-2" htmlFor="mostrarExtendido">
                                Ver Registro de turnos Extendido
                            </label>
                        </div>
                    </div>

                    {/* Accesos de coordinación — una sola línea, con scroll horizontal si no alcanza.
                        pt-3 deja aire arriba para que overflow-auto no recorte el badge de pendientes. */}
                    <div className="mb-3 d-flex flex-nowrap gap-1 overflow-auto pt-3 pb-1">
                        <button
                            className="btn btn-sm btn-outline-primary position-relative text-nowrap"
                            title="Solicitudes de Reemplazo"
                            onClick={() => setIsSolicitudesReemplazoOpen(true)}
                        >
                            <i className="fas fa-exchange-alt me-1"></i>
                            Reemplazos
                            {solicitudesPendientes > 0 && (
                                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                                    {solicitudesPendientes}
                                </span>
                            )}
                        </button>
                        <button
                            className="btn btn-sm btn-outline-success text-nowrap"
                            title="Acuerdos Mutuos"
                            onClick={() => {
                                setAccionAcuerdoMutuo("");
                                setContextoAcuerdoMutuo(null);
                                setIsAcuerdosMutuosOpen(true);
                            }}
                        >
                            <i className="fas fa-file-contract me-1"></i>
                            Acuerdos
                        </button>
                        <button
                            className="btn btn-sm btn-outline-warning text-nowrap"
                            title="Novedades"
                            onClick={() => setIsNovedadesOpen(true)}
                        >
                            <i className="bi bi-megaphone me-1"></i>
                            Novedades
                        </button>
                        <button
                            className="btn btn-sm btn-outline-info text-nowrap"
                            title="Patrones de Turnos"
                            onClick={() => setIsPatronesRotacionOpen(true)}
                        >
                            <i className="bi bi-diagram-3 me-1"></i>
                            Patrones
                        </button>
                        <button
                            className="btn btn-sm btn-outline-secondary text-nowrap"
                            title="Visitas Paciente"
                            onClick={() => setIsVisitasPacienteOpen(true)}
                        >
                            <i className="bi bi-house-heart me-1"></i>
                            Visitas
                        </button>
                        <button
                            className="btn btn-sm btn-outline-secondary text-nowrap"
                            title="Planificación Sectores"
                            onClick={() => setIsPlanificacionSectorOpen(true)}
                        >
                            <i className="bi bi-map me-1"></i>
                            Sectores
                        </button>
                        <button
                            className="btn btn-sm btn-outline-secondary text-nowrap"
                            title="Búsqueda Auxiliar"
                            onClick={() => setIsBusquedaAuxiliarOpen(true)}
                        >
                            <i className="bi bi-search me-1"></i>
                            Buscar Auxiliar
                        </button>
                        <button
                            className="btn btn-sm btn-outline-dark text-nowrap"
                            title="Nómina de Auxiliares"
                            onClick={() => setIsNominaAuxiliaresOpen(true)}
                        >
                            <i className="bi bi-people me-1"></i>
                            Nómina
                        </button>
                    </div>

                    {/* Tabla de turnos */}
                    <GdTable
                        columns={columns}
                        data={turnos}
                        itemsPerPageOptions={[50, 100, 300, 1000, 2000, 4000, 6000]} // Opciones de elementos por página
                        defaultItemsPerPage={20000}
                        // initialSort={[0, "asc"]}
                        tableClassName="table table-bordered"
                        showCsvExport={true}
                        height={700}
                        separadorPorCampo="pacienteNombre"
                        virtualize
                        getRowClass={(row) => {
                            const colorManual = Number(row?.color ?? 0);
                            if (colorManual === 1) return 'bg-row-blanco';

                            // === Eliminado ===
                            if (row?.es_eliminado) return 'bg-row-eliminado';

                            // === NUEVO: Colores Manuales (2: Azul, 3: Amarillo, 1: Ninguno) ===
                            // Si está manual el manual manda
                            if (colorManual === 2) return 'bg-row-reemplazo'; // Azul manual
                            if (colorManual === 3) return 'bg-row-marca';     // Amarillo manual

                            // === FUERA DE STGO (Prioridad Automática) ===
                            // Si el paciente vive fuera de santiago, el turno es verde

                            if (Array.isArray(row?.adicionales)) {
                                const esFueraStgo = row.adicionales.some(a => {
                                    const horasExtras = Number(a.horasExtras ?? 0);
                                    const valorTurno = Number(a.valorTurno ?? 0);
                                    return (a.fueraStgo === 1 && horasExtras === 0 && valorTurno !== 0);
                                });
                                if (esFueraStgo) return "bg-row-fuera-stgo";
                            }

                            // === Solicitud de reemplazo (Pendiente) ===
                            if (row?.solicitud_reemplazo && row.solicitud_estado == 0)
                                return 'bg-row-solicitud-reemplazo';

                            if (row?.solicitud_reemplazo)
                                return 'bg-row-reemplazo';

                            // === Reemplazo / Apoyo (Asignado - Prioridad sobre marca automática) ===
                            if (row?.es_reemplazo || row?.es_apoyo) return 'bg-row-reemplazo';


                            // === Marca amarillo automático ===
                            if (row?.marca) return 'bg-row-marca';

                            if (row?.vive_fuera_santiago == 1) return 'bg-row-verde';

                            // === NUEVO: Múltiples Adicionales (Warning) ===
                            if (row?.adicionales?.length > 1) return 'bg-row-rojo-claro';

                            return '';
                        }}


                        exportFilename={`registro-turnos-${startMonth}.xlsx`}
                    />

                    {turnos.length === 0 && (
                        <div className="alert alert-info">No se encontraron turnos que coincidan con la búsqueda.</div>
                    )}
                </div>
            </div>
            {isReemplazoOpen && (
                <ReemplazarAuxiliar
                    isOpen={isReemplazoOpen}
                    row={selectedRow}
                    onClose={() => setIsReemplazoOpen(false)}
                    onConfirm={manejarConfirmAccion}
                    periodo={startMonth}
                />
            )}
            {isAdicionalOpen && (
                <IngresoAdicional
                    isOpen={isAdicionalOpen}
                    row={selectedRow}
                    onClose={() => setIsAdicionalOpen(false)}
                    onConfirm={manejarConfirmAccion}
                    onOpenRecordatorioValor={handleOpenRecordatorioValor}
                />
            )}
            {isNotaOpen && (
                <NotaModal
                    isOpen={isNotaOpen}
                    row={selectedRow}
                    onClose={() => setIsNotaOpen(false)}
                    onConfirm={manejarConfirmAccion}
                />
            )}
            {isEliminarNotaOpen && (
                <EliminarNotaModal
                    isOpen={isEliminarNotaOpen}
                    row={selectedRow}
                    periodo={startMonth}
                    onClose={() => setIsEliminarNotaOpen(false)}
                    onConfirm={manejarConfirmAccion}
                />
            )}
            {isValorAdicionalOpen && (
                <ModalValorAdicional
                    isOpen={isValorAdicionalOpen}
                    row={selectedRow}
                    onClose={() => setIsValorAdicionalOpen(false)}
                    onConfirm={manejarConfirmAccion}
                />
            )}
            {isAdicionalesModalOpen && (
                <ModalAdicionales
                    isOpen={isAdicionalesModalOpen}
                    row={selectedRow}
                    onClose={() => setIsAdicionalesModalOpen(false)}
                    onEditar={(item) => {
                        // Preparamos el row con la info del adicional para editar
                        // Importante: asegurarnos de pasar el ID del adicional
                        const datosEdicion = {
                            ...selectedRow, // mantiene pacienteID, fecha base, etc.

                            // Guardamos el auxiliar original del turno para comparar
                            auxiliarTurnoID: selectedRow.auxiliarID,

                            ...item,        // sobreescribe con valores del adicional (valorTurno, horasExtras, etc)
                            idAdicional: item.adicionalID, // Para que el backend sepa que es edición

                            // Mapeo específico de campos si difieren nombres
                            // En ModalAdicionales el item tiene: valorTurno, valorColacion, etc.
                            // IngresoAdicional espera esos mismos nombres en 'row' o los carga en useEffect.

                            // Ajustar auxiliarID si el item tiene uno distinto
                            // Nota: IngresoAdicional usa row.auxiliarID o row.reemplazoID por defecto.
                            // Si el adicional tiene un auxiliar específico, hay que pasarlo.
                            auxiliarID: item.auxiliarID || selectedRow.auxiliarID,
                            auxiliarNombre: item.auxiliarNombre || selectedRow.auxiliarNombre,

                            // Ajustar RemplazoID (Paga SK / Cobro Paciente)
                            auxiliarRemplazoID: item.auxiliarRemplazoID,

                            // Ajustar fecha si la del adicional es distinta (aunque suele ser la misma o rango)
                            fecha: item.fechaAdicional || selectedRow.fecha,
                        };

                        setSelectedRow(datosEdicion);
                        setIsAdicionalesModalOpen(false); // Cerramos lista
                        setIsAdicionalOpen(true);         // Abrimos formulario
                    }}
                    onEliminar={async (adicionalID) => {
                        gdAlertify.confirm({
                            title: "Eliminar Adicional",
                            message: "¿Estás seguro de eliminar este adicional?",
                            onConfirm: async () => {
                                gdAlertify.showLoader();
                                try {
                                    const response = await axiosInstance.post(`/acuerdo-mutuo/adicional/eliminar/${adicionalID}`);
                                    if (!response.data.error) {
                                        gdAlertify.notiSuccess("Adicional eliminado correctamente.");
                                        refrescarFila(selectedRow?.pacienteID, selectedRow?.fecha);
                                        setIsAdicionalesModalOpen(false);
                                    } else {
                                        gdAlertify.notiError(response.data.message);
                                    }
                                } catch (err) {
                                    gdAlertify.notiError("Error al eliminar el adicional.");
                                } finally {
                                    gdAlertify.hideLoader();
                                }
                            },
                        });
                    }}
                />
            )}
            {isEliminarOpen && (
                <EliminarTurnoModal
                    isOpen={isEliminarOpen}
                    row={selectedRow}
                    periodo={startMonth}
                    onClose={() => setIsEliminarOpen(false)}
                    onConfirm={manejarConfirmAccion}
                />
            )}
            {isActualizarValoresOpen && (
                <ActualizarValoresModal
                    isOpen={isActualizarValoresOpen}
                    row={selectedRow}
                    periodo={startMonth}
                    onClose={() => setIsActualizarValoresOpen(false)}
                    onConfirm={manejarConfirmAccion}
                />
            )}
            {isDescuentoHorasOpen && (
                <DescuentoHorasExtraModal
                    isOpen={isDescuentoHorasOpen}
                    row={selectedRow}
                    onClose={() => setIsDescuentoHorasOpen(false)}
                    onConfirm={manejarConfirmAccion}
                />
            )}
            {isRecordatoriosOpen && (
                <ModalRecordatoriosPaciente
                    isOpen={isRecordatoriosOpen}
                    onClose={() => {
                        setIsRecordatoriosOpen(false);
                        // Recargar la tabla si es necesario para actualizar el icono de la campana
                        // obtenerTurnos(startMonth, searchPatient);
                    }}
                    pacienteId={recordatorioPacienteId}
                    pacienteNombre={recordatorioPacienteNombre}
                    onSaved={() => obtenerTurnos(startMonth, searchPatient)}
                />
            )}
            {recordatorioValorDetalle && (
                <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.25)" }} tabIndex="-1">
                    <div className="modal-dialog modal-sm modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header py-2">
                                <h6 className="modal-title mb-0">{recordatorioValorDetalle.titulo}</h6>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setRecordatorioValorDetalle(null)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {recordatorioValorDetalle.pacienteNombre && (
                                    <div className="fw-bold small mb-2">{recordatorioValorDetalle.pacienteNombre}</div>
                                )}
                                <div style={{ whiteSpace: "pre-wrap" }}>{recordatorioValorDetalle.texto}</div>
                            </div>
                            <div className="modal-footer py-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    onClick={() => setRecordatorioValorDetalle(null)}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {isEliminarAdicionalesMasivoOpen && (
                <EliminarAdicionalesMasivoModal
                    isOpen={isEliminarAdicionalesMasivoOpen}
                    periodo={startMonth}
                    pacienteID={selectedRow?.pacienteID}
                    pacienteNombre={selectedRow?.pacienteNombre}
                    rowFecha={selectedRow?.fecha}
                    onClose={() => setIsEliminarAdicionalesMasivoOpen(false)}
                    onConfirm={() => { obtenerTurnos(startMonth, searchPatient); }}
                />
            )}
            {isObservationModalOpen && (
                <ObservationAuxiliarModal
                    isOpen={isObservationModalOpen}
                    onClose={() => setIsObservationModalOpen(false)}
                    auxiliarID={selectedRow?.auxiliarID}
                    auxiliarNombre={
                        selectedRow?.reemplazoNombre && selectedRow?.auxiliarID != null && selectedRow?.es_reemplazo == 0
                            ? selectedRow?.reemplazoNombre
                            : selectedRow?.auxiliarNombre
                    }
                />
            )}
            {isNoEnviarOpen && (
                <NoEnviarPacienteAuxiliarModal
                    isOpen={isNoEnviarOpen}
                    onClose={() => {
                        setIsNoEnviarOpen(false);
                        setNoEnviarContext(null);
                    }}
                    onChange={() => {
                        obtenerTurnos(startMonth, searchPatient);
                    }}
                    auxiliarID={noEnviarContext?.auxiliarID}
                    nombreAuxiliar={noEnviarContext?.nombreAuxiliar}
                    pacienteID={noEnviarContext?.pacienteID}
                    nombrePaciente={noEnviarContext?.pacienteNombre}
                    fechaTurno={noEnviarContext?.fechaTurno}
                />
            )}
            {isRetiroPacienteOpen && (
                <RetirarPacienteTurnoModal
                    isOpen={isRetiroPacienteOpen}
                    row={selectedRow}
                    onClose={() => setIsRetiroPacienteOpen(false)}
                    // "Retirar" desactiva al paciente hacia adelante: puede afectar
                    // muchas fechas futuras, no solo esta fila. Se mantiene el
                    // refresco completo a propósito, no uses refrescarFila aquí.
                    onConfirm={() => obtenerTurnos(startMonth, searchPatient)}
                />
            )}
            <EditarPacienteModal
                isOpen={isEditarPacienteOpen}
                pacienteId={pacienteEditarId}
                onClose={() => {
                    setIsEditarPacienteOpen(false);
                    setPacienteEditarId(null);
                }}
                onSaved={() => {
                    setIsEditarPacienteOpen(false);
                    setPacienteEditarId(null);
                    gdAlertify.notiSuccess("Paciente y tutores actualizados");
                    limpiarBackdropsHuerfanos();
                }}
            />
            <EditarAuxiliarModal
                isOpen={isEditarAuxiliarOpen}
                auxiliarId={auxiliarEditarId}
                onClose={() => {
                    setIsEditarAuxiliarOpen(false);
                    setAuxiliarEditarId(null);
                }}
                onSaved={() => {
                    setIsEditarAuxiliarOpen(false);
                    setAuxiliarEditarId(null);
                    obtenerTurnos(startMonth, searchPatient);
                    gdAlertify.notiSuccess("Auxiliar actualizado");
                    limpiarBackdropsHuerfanos();
                }}
            />
            <ResumenPacienteModal
                isOpen={isResumenPacienteOpen}
                pacienteId={resumenPacienteId}
                onClose={() => {
                    setIsResumenPacienteOpen(false);
                    setResumenPacienteId(null);
                    limpiarBackdropsHuerfanos();
                }}
            />
            <ColorTurnoModal
                isOpen={isColorOpen}
                onClose={() => setIsColorOpen(false)}
                onConfirm={manejarConfirmAccion}
                row={selectedRow}
                periodo={startMonth}
            />
            <VacacionesModal
                isOpen={isVacacionesOpen}
                onClose={() => setIsVacacionesOpen(false)}
                auxiliarID={auxiliarForVacaciones?.id}
                nombreAuxiliar={auxiliarForVacaciones?.nombreAuxiliar}
            />
            <ModalSolicitudesReemplazo
                isOpen={isSolicitudesReemplazoOpen}
                onClose={() => setIsSolicitudesReemplazoOpen(false)}
                onRefresh={() => {
                    obtenerTurnos(startMonth, searchPatient);
                    obtenerSolicitudesPendientes();
                }}
            />
            <ModalAcuerdosMutuos
                isOpen={isAcuerdosMutuosOpen}
                accionInicial={accionAcuerdoMutuo}
                contextoInicial={contextoAcuerdoMutuo}
                onClose={() => {
                    setIsAcuerdosMutuosOpen(false);
                    setAccionAcuerdoMutuo("");
                    setContextoAcuerdoMutuo(null);
                }}
            />
            {isNovedadesOpen && (
                <Novedades
                    modal
                    onClose={() => {
                        setIsNovedadesOpen(false);
                        limpiarBackdropsHuerfanos();
                    }}
                />
            )}
            {isPatronesRotacionOpen && (
                <PatronesRotacion
                    modal
                    onClose={() => {
                        setIsPatronesRotacionOpen(false);
                        limpiarBackdropsHuerfanos();
                    }}
                />
            )}
            {isVisitasPacienteOpen && (
                <VisitasPacienteCoordinacion
                    modal
                    onClose={() => {
                        setIsVisitasPacienteOpen(false);
                        limpiarBackdropsHuerfanos();
                    }}
                />
            )}
            {isPlanificacionSectorOpen && (
                <PlanificacionSector
                    modal
                    onClose={() => {
                        setIsPlanificacionSectorOpen(false);
                        limpiarBackdropsHuerfanos();
                    }}
                />
            )}
            {isBusquedaAuxiliarOpen && (
                <BusquedaAuxiliar
                    modal
                    onClose={() => {
                        setIsBusquedaAuxiliarOpen(false);
                        limpiarBackdropsHuerfanos();
                    }}
                />
            )}
            {/* NominaAuxiliares no soporta prop `modal`, se envuelve en un shell propio */}
            {isNominaAuxiliaresOpen && (
                <>
                    <div className="modal fade show" style={{ display: "block", zIndex: 1055 }} tabIndex="-1">
                        <div className="modal-dialog modal-fullscreen-lg-down" style={{ maxWidth: "95vw", margin: "2vh auto" }}>
                            <div className="modal-content" style={{ maxHeight: "96vh" }}>
                                <div className="modal-header bg-dark text-white py-2">
                                    <h6 className="modal-title">
                                        <i className="bi bi-people me-2"></i>
                                        Nómina de Auxiliares
                                    </h6>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() => {
                                            setIsNominaAuxiliaresOpen(false);
                                            limpiarBackdropsHuerfanos();
                                        }}
                                    ></button>
                                </div>
                                <div className="modal-body p-0" style={{ overflowY: "auto" }}>
                                    <NominaAuxiliares />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1050 }}></div>
                </>
            )}
        </div>
    );
};

export default CalendarioTurnos;
